/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Route, Switch, useHistory } from 'react-router-dom';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useStyles } from '~src/hooks/useStyles';
import { styleFn } from './style';
import ResizeObserver from 'resize-observer-polyfill';
import RightSideViewSuTemplates from '../RightSideViewSuTemplates/RightSideViewSuTemplates';
import { RightSideViewSuTemplateFolders } from '../RightSideViewSuTemplateFolders/RightSideViewSuTemplateFolders';
import RightSideViewProjectBuilder from '../RightSideViewProjectBuilder/RightSideViewProjectBuilder';
import { DeletedFilesTable } from '~components/atoms/FileTables/DeletedFilesTable';
import { RightClickOverlay } from '~components/atoms/RightClickOverlay/RightClickOverlay';
import { DbFile, DbFolder, DbModuleFile } from '~src/types/db';
import { useItemPermission } from '~src/hooks/useItemPermission';
import dataContextMenu from '~src/data/dataContextMenu';
import {
  handleSelectCtrl,
  TableEntry,
} from '~components/atoms/FileTables/FileTable.utils';
import { scrollIntoView } from '~src/utils/ui';
import { downloadFileLastVersion, downloadFolder } from '~src/utils/download';
import FileVersionsContext from '~src/contexts/FileVersionsContext';
import { message } from 'antd';
import { useIntl } from 'react-intl';
import { appLog } from '~src/utils/log';
import { debounce, uniqBy } from 'lodash';
import {
  FileTable,
  FileTableProps,
} from '~components/atoms/FileTables/FileTable';
import { useProjectId } from '~src/hooks/useProjectId';
import UploadPanel from '~components/atoms/UploadPanel/UploadPanel';
import CherryPickPanel from '~components/atoms/CherryPickPanel/CherryPickPanel';
import PropertiesPanel from '~components/atoms/PropertiesPanel/PropertiesPanel';
import { useIsPath } from '~src/hooks/useIsPath';
import { EditFileModal } from '~components/popups-modals/EditFile.modal';
import MoveToModal from '~components/popups-modals/MoveToModal/MoveToModal';
import { RestoreToModal } from '~components/popups-modals/RestoreTo.modal';
import {
  addToFavoriteFiles,
  changeFileState,
  ChangeFileStateParam,
  editFile,
  GetTenderAttachmentResponse,
  removeFromFavoriteFiles,
  requestFileLocation,
} from '~src/services/api';
import { queryCache } from 'react-query';
import { DB_STATE, RIGHTS_LEVEL } from '~src/types/enums';
import { DeleteModal } from '~components/popups-modals/Delete.modal';
import { SearchTable } from '~components/atoms/FileTables/SearchTable';
import { CreateFolderModal } from '~components/popups-modals/CreateFolder.modal';
import { ShareModal } from '~components/popups-modals/Share.modal';
import { EditFolderModal } from '~components/popups-modals/EditFolder.modal';
import { RightsModal } from '~components/popups-modals/RightsModal/RightsModal';
import { FolderPropertiesModal } from '~components/popups-modals/FolderPropertiesModal/FolderPropertiesModal';
import { ItemLogModal } from '~components/popups-modals/ItemLog.modal';
import { useFileViewerContext } from '~src/contexts/FileToViewContext';
import Viewer from '~components/atoms/Viewers/Viewer';
import { parseFileName } from '~src/utils/file';
import { MoveModal } from '~components/popups-modals/Move.modal';
import { useCurrentFolder } from '~src/hooks/useCurrentFolder';
import { updateCachedFile } from '~src/services/cache/files';
import { useToggleFavouriteFolder } from '~src/hooks/actions/useToggleFavouriteFolder';
import LargeFilesContext from '~src/contexts/LargeFilesContext';
import { useFolders } from '~src/hooks/api-hooks/useFolders';
import { GetLinkModuleFileParams } from '../RightSideView/RightSideView';
import { getLinkModuleFile } from '~src/services/api/modules';
import UploadContext from '~src/contexts/UploadContext';
import { DownloadingState, useStore } from '~src/store';
import { SVFViewerModal } from '~components/popups-modals/SVFViewer.modal';

export const RightSideViewSu: React.FC = () => {
  const style = useStyles(styleFn);
  const intl = useIntl();
  const history = useHistory();
  const projectId = useProjectId();
  const isOnDeletedFiles = useIsPath('/deleted-files');
  const isOnTemplates = useIsPath('templates');
  const isOnProjectBuilder = useIsPath('/project-builder');
  const { fileVersionsContext } = useContext(FileVersionsContext);
  const { uploadContext } = useContext(UploadContext);
  const { setLargeFilesContext } = useContext(LargeFilesContext);
  const currentFolder = useCurrentFolder();
  const folderId = currentFolder?.id || null;
  const [bottomViewHeight, setBottomViewHeight] = useState(0);
  const { toggleFavouriteFolder } = useToggleFavouriteFolder();

  const resizeObserverRef = useRef(
    new ResizeObserver(
      debounce(([resizeEntry]) => {
        // update `bottomViewHeight` based on "zoneDiv" height
        setBottomViewHeight(resizeEntry.contentRect.height);
      }, 50)
    )
  );

  const [rightClickEntity, setRightClickEntity] = useState<{
    folder?: DbFolder;
    file?: DbFile;
    noFiles?: boolean;
  }>({});

  const [rightClickEntityOverlay, setRightClickEntityOverlay] = useState<{
    folder?: DbFolder;
    file?: DbFile;
    noFiles?: boolean;
  }>({});

  const [fileRevision, setFileRevision] = useState<{
    id?: string;
    revision?: number | null;
  }>({
    id: undefined,
    revision: null,
  });

  const rightClickEntityParentPermission = useItemPermission({
    folder: rightClickEntity.folder,
    file: rightClickEntity.file,
  });

  const [selectedItems, setSelectedItems] = useState<TableEntry[]>([]);
  const [selectedForDetails, setSelectedForDetails] = useState<TableEntry>();
  const { fileToView, setFileToView } = useFileViewerContext();
  const [svfModal, setSVFModal] = useState(false);
  const [svfModalForceViewer, setSVFModalForceViewer] = useState(false);
  const [svfModelFileToView, setSVFModalFileToView] = useState<
    DbFile | DbModuleFile | undefined
  >(undefined);

  const clickTimeoutRef = useRef<number>();
  const zoneDivRef = useRef<HTMLDivElement>(null);

  // state for all modals
  const [shareModal, setShareModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [folderEditModal, setFolderEditModal] = useState(false);
  const [moveModal, setMoveModal] = useState(false);
  const [itemLogModal, setItemLogModal] = useState(false);
  const [createFolderModal, setCreateFolderModal] = useState(false);
  const [folderPropertiesModal, setFolderPropertiesModal] = useState(false);
  const [fileEditModal, setFileEditModal] = useState(false);
  const [folderRightsModal, setFolderRightsModal] = useState(false);
  const [restoreToModal, setRestoreToModal] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState<string>();

  const foldersQuery = useFolders({ as_admin: true });

  const selectedKeys: Required<FileTableProps>['selectedKeys'] = selectedItems.map(
    (item) => item.key
  );

  const handleSelect: Required<FileTableProps>['onSelect'] = (
    record,
    selected
  ) => {
    if (selectedForDetails) setSelectedForDetails(undefined);

    if (selected) {
      // insert item at start of arr
      setSelectedItems((prevSelectedItems) => {
        if (!prevSelectedItems.length) {
          // delay as we are within setState
          setTimeout(() => scrollToSelectedItem(record.key), 20);
        }
        return [record, ...prevSelectedItems];
      });
    } else {
      // remove item from arr
      setSelectedItems((prevSelectedItems) =>
        prevSelectedItems.filter((item) => item.key !== record.key)
      );
    }
  };

  const handleSelectAll: Required<FileTableProps>['onSelectAll'] = (
    selected,
    rows,
    changedRows
  ) => {
    if (selectedForDetails) setSelectedForDetails(undefined);

    // keep truthy values, because selected rows from other "folders" are included as `undefined` in the list
    const selectedRows = rows.filter(Boolean);

    if (selected) {
      // insert all items at start of arr, filter unique
      setSelectedItems((prevSelectedItems) =>
        uniqBy([...selectedRows, ...prevSelectedItems], 'key')
      );
    } else {
      // use `changedRows`, because `rows` is empty on "deselectAll"
      const rowKeys = changedRows.map((row) => row.key);
      // remove items from arr
      setSelectedItems((prevSelectedItems) =>
        prevSelectedItems.filter((item) => !rowKeys.includes(item.key))
      );
    }
  };

  let panelNode: React.ReactElement | undefined;
  if (uploadContext.length) {
    panelNode = <UploadPanel key="upload" />;
  } else if (selectedItems.length) {
    panelNode = (
      <CherryPickPanel
        key="cherry-picker"
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    );
  }

  // attach resize observer on zoneDiv
  useEffect(() => {
    const div = zoneDivRef.current;

    if (div) {
      const resizeObserver = resizeObserverRef.current; // ResizeObserver instance
      resizeObserver.observe(div); // add listener

      // cleanup -> remove listener
      return () => resizeObserver.unobserve(div);
    }
  }, [panelNode?.key]);

  const { changeDownloadingActive, changeDownloadingState } = useStore();

  const rightClickAction = (modal: string, option?: number) => {
    const modalsMap = {
      share: setShareModal,
      'folder-edit': setFolderEditModal,
      'folder-move': setMoveModal,
      'folder-properties': setFolderPropertiesModal,
      'rights-managment': setFolderRightsModal,
      'file-edit': setFileEditModal,
      'file-move': setMoveModal,
      'item-log': setItemLogModal,
      'folder-create': setCreateFolderModal,
      delete: setDeleteModal,
      'restore-to': setRestoreToModal,
    };

    // set modal visible
    if (modalsMap[modal]) {
      modalsMap[modal](true);
    } else {
      // non-modal actions
      if (modal === 'svf-view-file') {
        if (!rightClickEntity.file) return;

        if (option === 2) {
          // Windowed viewer (no modal)
          setFileToView({ ...rightClickEntity.file });
          return;
        }

        if (option === 3) {
          // Fullscreen (force viewer inside modal)
          setSVFModalFileToView(rightClickEntity.file);
          setSVFModal(true);
          setSVFModalForceViewer(true);
          return;
        }

        // 0: Convert / Retry, 1: Progress (open modal pinned to this file)
        setSVFModalFileToView(rightClickEntity.file);
        setSVFModal(true);
        setFileToView(undefined);
        return;
      }

      if (modal === 'file-download') {
        if (!rightClickEntity.file) return;
        return downloadFileLastVersion(rightClickEntity.file, () =>
          message.info(intl.formatMessage({ id: 'download-starting' }))
        ).catch((err) => {
          appLog('downloadFile -> err', err);
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }
      if (modal === 'restore') {
        if (!rightClickEntity.file) return;
        return restoreFile({
          projectId: projectId as number,
          registrationId: String(rightClickEntity.file.id),
          stateValue: DB_STATE.AVAILABLE,
        }).catch((err) => {
          appLog('restoreFile -> err', err);
          message.error(intl.formatMessage({ id: 'restore-file-failed' }));
        });
      }

      if (modal === 'folder-download') {
        if (!rightClickEntity.folder) return;

        // activate download progress panel
        changeDownloadingActive(true);
        changeDownloadingState(DownloadingState.PREPARING);

        // const hidePrepMsg = message.loading(
        //   intl.formatMessage({ id: 'download-preparing' }),
        //   0
        // );
        return downloadFolder({
          folder: rightClickEntity.folder,
          allFolders: foldersQuery.data || [],
          fileVersionsContext,
          onDownloadStarted: () => {
            // hidePrepMsg();
            setTimeout(
              () =>
                message.info(intl.formatMessage({ id: 'download-starting' })),
              400
            );
          },
          onLargeFiles: (largeFiles) => setLargeFilesContext(largeFiles),
        }).catch((err) => {
          appLog('downloadFolder -> err', err);
          // hidePrepMsg && hidePrepMsg();
          message.error(intl.formatMessage({ id: 'file-download-failed' }));
        });
      }

      if (modal === 'favourite') {
        toggleFavourite();
      }
    }
  };

  /** Toggle `is_favourite` prop, for selected resource */
  const toggleFavourite = async () => {
    if (rightClickEntity.file) {
      const fileId = rightClickEntity.file.id;
      const nextIsFavourite = !rightClickEntity.file.is_favorite;

      try {
        if (nextIsFavourite) {
          await addToFavoriteFiles({
            data: {
              ...rightClickEntity.file,
              is_favorite: nextIsFavourite,
            },
          });
        } else {
          await removeFromFavoriteFiles({
            data: {
              ...rightClickEntity.file,
              is_favorite: nextIsFavourite,
            },
          });
        }

        // to void dropdown stutter
        setTimeout(() => {
          // show immediate UI update
          updateCachedFile(
            fileId,
            { is_favorite: nextIsFavourite },
            { projectId: String(projectId), withVersions: true }
          );
          queryCache.invalidateQueries('files');
        }, 200);
      } catch (err) {
        nextIsFavourite
          ? appLog('RightSideViewSu -> addToFavoriteFiles -> err', err)
          : appLog('RightSideViewSu -> removeFromFavoriteFiles -> err', err);
        message.error(intl.formatMessage({ id: 'favourite-failed' }));
      }
    } else if (rightClickEntity.folder) {
      toggleFavouriteFolder(rightClickEntity.folder);
    }
  };

  const restoreFile = async ({
    projectId,
    registrationId,
    stateValue,
  }: ChangeFileStateParam) => {
    try {
      const changedFile = await changeFileState({
        projectId,
        registrationId,
        stateValue,
      });
      queryCache.invalidateQueries('files');
      message.success(intl.formatMessage({ id: 'restore-success' }));
      return changedFile;
    } catch (err) {
      appLog('err => ', err);
      return;
    }
  };

  const tableScroll: Required<FileTableProps>['scroll'] = {
    // satisfies sum of fixed width columns + reasonable dynamic "name" column
    x: 1065,
    // 141px is topBar + folderNameBar + tableHeader + scrollbar
    y: `calc(100vh - 121px - ${bottomViewHeight}px)`,
  };

  /**
   * Row right-click handler
   *
   * sets rightClickEntity for context menu
   */
  const handleSelectedFileContext = (record: TableEntry | undefined) => {
    if (record) {
      if (record.folder && record.dbFolder) {
        setRightClickEntity({ folder: record.dbFolder });
        setRightClickEntityOverlay({ folder: record.dbFolder });
      } else {
        setRightClickEntity({ file: record.dbFile });
        setRightClickEntityOverlay({ file: record.dbFile });
      }

      setSelectedForDetails(record);
    } else {
      setRightClickEntity({ noFiles: true });
      setRightClickEntityOverlay({ noFiles: true });
    }
  };

  const handleFolderDoubleClick = (record: TableEntry) => {
    window.clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = undefined;
    history.push(`/projects/${projectId}/archive/${record.key}`);
  };

  const handleFileDoubleClick = (record: TableEntry) => {
    // console.log(`RighSideViewSu`, fileVersionsContext);
    if (record.dbFile) {
      downloadFileLastVersion(record.dbFile, fileVersionsContext, () =>
        message.info(intl.formatMessage({ id: 'download-starting' }))
      ).catch((err) => {
        appLog('downloadFile -> err', err);
        message.error(intl.formatMessage({ id: 'file-download-failed' }));
      });
    }
  };
  const toggleItemSelected = (item: TableEntry) => {
    setSelectedItems((prevItems) => {
      const nextItems = [...prevItems];
      const index = nextItems.findIndex((v) => v.key === item.key);

      if (index > -1) {
        // found -> remove item
        nextItems.splice(index, 1);
      } else {
        nextItems.unshift(item); // insert at start of array
      }

      return nextItems;
    });
  };

  /** Table `row` callback;
   * @returns object containing event handlers, settings
   */
  const handleRow: Required<FileTableProps>['onRow'] = (record) => ({
    onClick: (event) => {
      if (event.ctrlKey || event.metaKey) {
        return handleSelectCtrl({ record, setSelectedItems, selectedItems });
      }
      return handleRowClick(record);
    },
    onContextMenu: () =>
      !selectedItems.length && handleSelectedFileContext(record),
    onDoubleClick: record.folder
      ? () => handleFolderDoubleClick(record)
      : () => handleFileDoubleClick(record),
    // Deleted by task http://jira.detaljen.lan/browse/CPT-33
    // title: record.disabled
    //   ? intl.formatMessage({ id: 'item-unfinished' })
    //   : undefined,
  });

  const handleRowClick = (record: TableEntry) => {
    // if (clickTimeoutRef.current) return;
    // clickTimeoutRef.current = window.setTimeout(() => {
    // clickTimeoutRef.current = undefined;

    if (!selectedItems.length) {
      return setSelectedForDetails((prevRecord) =>
        prevRecord?.key === record.key ? undefined : record
      );
    }

    if (!record.disabled) {
      toggleItemSelected(record);
    }
    // }, 300);
  };

  const renderFileTable = (props) =>
    renderFile(
      <FileTable
        {...props}
        scroll={tableScroll}
        highlightedRow={selectedForDetails}
        selectedKeys={selectedKeys}
        onSelect={handleSelect}
        suMode={true}
        onSelectAll={handleSelectAll}
        handleRow={handleRow}
        setSelectedItems={setSelectedItems}
        onEmptyOverlay={() => {
          setRightClickEntity({
            file: undefined,
            folder: undefined,
            noFiles: true,
          });
          setRightClickEntity({
            file: undefined,
            folder: undefined,
            noFiles: true,
          });
        }}
        openSVFTranscribe={(fileToView) => {
          setSVFModal(true);
          setSVFModalFileToView(fileToView);
        }}
      />
    );

  const getModuleUrl = async ({
    projectId,
    moduleFileId,
    moduleId,
    response_type,
  }: GetLinkModuleFileParams) => {
    const url = await getLinkModuleFile({
      moduleFileId,
      projectId,
      moduleId,
      response_type,
    });
    setSelectedUrl(url);
    return url;
  };

  const isGetTenderAttachmentResponse = (
    obj: any
  ): obj is GetTenderAttachmentResponse => {
    return obj && obj.location !== undefined;
  };

  useEffect(() => {
    if (
      !isGetTenderAttachmentResponse(fileToView) &&
      fileToView &&
      (fileToView.hasOwnProperty('versions') ||
        fileToView.hasOwnProperty('location'))
    ) {
      if (
        fileRevision.id === fileVersionsContext[fileToView.id]?.id &&
        fileRevision.id !== undefined &&
        fileRevision.revision ===
          fileVersionsContext[fileToView.id]?.revision &&
        fileRevision.revision !== undefined
      ) {
        return;
      } else {
        setFileRevision({
          id: fileVersionsContext[fileToView.id]?.id,
          revision: fileVersionsContext[fileToView.id]?.revision,
        });
      }
      const selectedIndex = fileVersionsContext[fileToView.id]
        ? fileToView.versions?.findIndex(
            (opt) => opt.id === fileVersionsContext[fileToView.id]?.id
          )
        : 0;

      // clear previous url
      setSelectedUrl(undefined);

      // if module-file
      if (fileToView.hasOwnProperty('location')) {
        getModuleUrl({
          moduleFileId: String(fileToView.id),
          projectId: String(projectId),
          moduleId: (fileToView as DbModuleFile).module,
          response_type: 'base64',
        });
      } else if ((selectedIndex as number) >= 0) {
        const getFileToViewLocation = async () => {
          if (fileToView.versions) {
            const getFileLocation = await requestFileLocation({
              fileId: fileToView.versions[selectedIndex as number].id,
              projectId:
                fileToView.versions[selectedIndex as number].project_id,
              versionedFileId:
                fileToView.versions[selectedIndex as number].versioned_file_id,
            });
            return getFileLocation;
          }
        };
        const fileLocation = getFileToViewLocation();
        const setFileLocation = async () => {
          setSelectedUrl(await fileLocation);
        };
        setFileLocation();
      }
    } else {
      setSelectedUrl(undefined);
    }
  }, [fileToView, fileVersionsContext]);

  // STAGING OLD LOGIC?:
  //   // if module-file
  //   if (fileToView.hasOwnProperty('location')) {
  //     getModuleUrl({
  //       moduleFileId: String(fileToView.id),
  //       projectId: String(projectId),
  //       moduleId: (fileToView as DbModuleFile).module,
  //       response_type: 'base64',
  //     });
  //     return;
  //   }
  //
  //   const rawIndex = fileToView.versions?.findIndex(
  //     (opt) => opt.id === fileVersionsContext[fileToView.id]?.id
  //   );
  //   const selectedIndex = rawIndex == null || rawIndex < 0 ? 0 : rawIndex;
  //
  //   // get file url
  //   const getFileToViewLocation = async () => {
  //     try {
  //       const v = fileToView.versions?.[selectedIndex];
  //       if (!v) return;
  //
  //       const url = await requestFileLocation({
  //         fileId: v.id,
  //         projectId: v.project_id,
  //         versionedFileId: v.versioned_file_id,
  //       });
  //
  //       setSelectedUrl(url); // after successfull spleet
  //     } catch (err) {
  //       appLog('requestFileLocation (SU) -> err', err);
  //       message.error(intl.formatMessage({ id: 'file-preview-error' }));
  //       setSelectedUrl(undefined);
  //     }
  //   };
  //
  //   getFileToViewLocation();
  // } else {
  //   setSelectedUrl(undefined);
  // }
  // }, [fileToView, fileVersionsContext]);

  const renderFile = (content: JSX.Element) => {
    if (
      !isGetTenderAttachmentResponse(fileToView) &&
      fileToView &&
      (fileToView.versions || (fileToView as DbModuleFile).location) &&
      selectedUrl &&
      !svfModal
    ) {
      const { extension } = parseFileName(
        fileToView?.['title']
          ? fileToView?.['title']
          : fileToView?.['file_name']
      );

      return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
          <div style={{ width: '50%', overflowX: 'scroll' }}>{content}</div>
          <div
            style={{
              width: '50%',
              height: `calc(100vh - 91px - ${bottomViewHeight}px)`,
              overflowX: 'scroll',
            }}
            onContextMenu={(e) => e.stopPropagation()}>
            <Viewer
              fileUrl={selectedUrl}
              fileExtension={extension}
              fileToView={fileToView as DbModuleFile}
              onCloseSVFViewer={() => setFileToView(undefined)}
            />
          </div>
        </div>
      );
    }
    return content;
  };

  const getRightClickItems = () => {
    if (rightClickEntity.folder) {
      return dataContextMenu.folder;
    } else if (rightClickEntity.file) {
      if (
        isOnDeletedFiles ||
        rightClickEntity.file.state === DB_STATE.REMOVED
      ) {
        return dataContextMenu.deletedFile;
      } else {
        return dataContextMenu.file;
      }
    } else if (rightClickEntity.noFiles) {
      return dataContextMenu.noItemSelected;
    } else {
      return [];
    }
  };

  return (
    <div css={style.container} onContextMenu={(e) => e.preventDefault()}>
      <RightClickOverlay
        leftSide={true}
        onOverlayClose={() => {
          setRightClickEntityOverlay({
            folder: undefined,
            file: undefined,
            noFiles: undefined,
          });
          setRightClickEntity({
            folder: undefined,
            file: undefined,
            noFiles: undefined,
          });
        }}
        permissionLevel={RIGHTS_LEVEL.SUPER_USER}
        items={getRightClickItems()}
        disabled={!!selectedItems.length}
        selected={rightClickEntityOverlay}
        onItemClick={(item, option) => {
          rightClickAction(item.modal, option);
          setRightClickEntityOverlay({
            folder: undefined,
            file: undefined,
            noFiles: undefined,
          });
          // setRightClickEntity({
          //   folder: undefined,
          //   file: undefined,
          //   noFiles: undefined,
          // });
        }}>
        <Switch>
          <Route
            path="/projects/:projectId/archive/:folderId"
            render={renderFileTable}
          />
          <Route path="/projects/:projectId/archive" render={renderFileTable} />

          <Route
            path="/projects/:projectId/search"
            render={(props) =>
              renderFile(
                <SearchTable
                  {...props}
                  suMode={true}
                  scroll={tableScroll}
                  highlightedRow={selectedForDetails}
                  selectedKeys={selectedKeys}
                  onSelect={handleSelect}
                  onSelectAll={handleSelectAll}
                  handleRow={handleRow}
                  setSelectedItems={setSelectedItems}
                  openSVFTranscribe={(fileToView) => {
                    setSVFModal(true);
                    setSVFModalFileToView(fileToView);
                  }}
                />
              )
            }
          />

          <Route
            exact
            path="/projects/:projectId/deleted-files"
            render={(props) =>
              renderFile(
                <DeletedFilesTable
                  {...props}
                  scroll={tableScroll}
                  highlightedRow={selectedForDetails}
                  selectedKeys={selectedKeys}
                  onSelect={handleSelect}
                  onSelectAll={handleSelectAll}
                  handleRow={handleRow}
                  setSelectedItems={setSelectedItems}
                  cleanSelected={() => setSelectedItems([])}
                  openSVFTranscribe={(fileToView) => {
                    setSVFModal(true);
                    setSVFModalFileToView(fileToView);
                  }}
                />
              )
            }
          />
          {/* Template admin routes hidden
          <Route
            path="/projects/:projectId/templates/:templateId"
            render={(props) => <RightSideViewSuTemplateFolders />}
          />
          <Route
            exact
            path="/projects/:projectId/templates"
            render={(props) => <RightSideViewSuTemplates />}
          />
          */}
          <Route
            exact
            path="/projects/:projectId/project-builder"
            render={() => <RightSideViewProjectBuilder />}
          />
        </Switch>
      </RightClickOverlay>

      <div css={style.panelsContainer} ref={zoneDivRef}>
        {/* panel bellow the table e.g. UploadPanel */}
        {panelNode
          ? panelNode
          : !isOnTemplates &&
            !isOnProjectBuilder && (
              <PropertiesPanel
                key="properties"
                selectedItem={selectedForDetails}
              />
            )}
      </div>

      <DeleteModal
        visible={deleteModal}
        files={rightClickEntity.file ? [rightClickEntity.file] : undefined}
        folders={
          rightClickEntity.folder ? [rightClickEntity.folder] : undefined
        }
        onCancel={() => setDeleteModal(false)}
        onDone={() => {
          setDeleteModal(false);
          setSelectedForDetails(undefined);
        }}
      />

      <MoveModal
        visible={moveModal}
        folderId={folderId}
        files={rightClickEntity.file ? [rightClickEntity.file] : undefined}
        folders={
          rightClickEntity.folder ? [rightClickEntity.folder] : undefined
        }
        onCancel={() => setMoveModal(false)}
        onDone={() => {
          setMoveModal(false);
          setSelectedForDetails(undefined);
        }}
      />

      <EditFileModal
        visible={fileEditModal}
        file={rightClickEntity.file}
        onCancel={() => {
          setFileEditModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
      />
      <RestoreToModal
        visible={restoreToModal}
        files={rightClickEntity.file ? [rightClickEntity.file] : []}
        onDone={() => {
          setRestoreToModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
      />

      <ShareModal
        visible={shareModal}
        files={rightClickEntity.file ? [rightClickEntity.file] : []}
        onCancel={() => {
          setShareModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        onDone={() => {
          setShareModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
      />

      <EditFolderModal
        visible={folderEditModal}
        folder={rightClickEntity.folder}
        onCancel={() => {
          setFolderEditModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
      />
      <RightsModal
        visible={folderRightsModal}
        onCancel={() => {
          setFolderRightsModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        folder={rightClickEntity.folder}
      />

      <FolderPropertiesModal
        visible={folderPropertiesModal}
        onCancel={() => {
          setFolderPropertiesModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        folder={rightClickEntity.folder}
      />

      <ItemLogModal
        visible={itemLogModal}
        onCancel={() => {
          setItemLogModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        file={rightClickEntity.file}
        folder={rightClickEntity.folder}
      />

      {/* SVF Modal (same behavior as in RightSideView) */}
      <SVFViewerModal
        visible={svfModal}
        fileToView={svfModelFileToView ?? rightClickEntity.file}
        forceViewer={svfModalForceViewer}
        onCancel={() => {
          queryCache.invalidateQueries('files');
          setSVFModalForceViewer(false);
          setSVFModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        onReloadData={() => {
          queryCache.invalidateQueries('files');
          setSVFModalForceViewer(false);
          setSVFModal(false);
          setRightClickEntity({ folder: undefined, file: undefined });
        }}
        onOpenWindowed={() => {
          setFileToView(svfModelFileToView ?? rightClickEntity.file);
          setSVFModal(false);
        }}
        onOpenFullscreen={() => {
          setSVFModal(true);
          setSVFModalForceViewer(true);
        }}
      />
    </div>
  );
};

/** Scroll table to record matching `key` */
const scrollToSelectedItem = (key: string) => {
  // get row elem & scroll
  const rowElem = document.querySelector(
    `.ant-table-row.ant-table-row-selected[data-row-key="${key}"]`
  );

  if (rowElem?.scrollIntoView) {
    setTimeout(() => scrollIntoView(rowElem), 100);
  }
};
